import { useState } from "react";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useAccountCallback, useAccountCallbackMutations } from "../api";
import { AccountCallbackForm } from "../AccountCallbackForm/AccountCallbackForm";
import { ServiceAccountNotice } from "../ServiceAccountNotice/ServiceAccountNotice";
import type { AccountCallbackFormValues } from "../types";

const FORM_ID = "account-callback-form";

export const Edit = (): JSX.Element => {
  const { callback, isLoading, refetch } = useAccountCallback();
  const { update } = useAccountCallbackMutations();
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<AccountCallbackFormValues>();
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) return <ScopeStackSpinner />;
  // Edit manages only an EXISTING legacy callback. No callback -> the same
  // service-accounts notice as the `new` route (creation is deprecated).
  if (!callback) return <ServiceAccountNotice />;

  const initialValues = savedInitial ?? { redirectUri: callback.redirectUri };

  const handleSubmit = async (values: AccountCallbackFormValues) => {
    setSubmitting(true);
    try {
      await update(callback.id, values);
      markSaved(values, "Callback URL saved");
      // No cache tags on the generated API -> refetch so re-entry is fresh.
      refetch();
    } catch (err) {
      console.error("Failed to update account callback", err);
      setToast({ variant: "danger", text: "Could not save callback URL." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="OAuth2 Callback URL"
        formId={FORM_ID}
        isSubmitting={submitting}
      >
        <AccountCallbackForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          clientId={callback.clientId}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
